/**
 * Date formatting utilities for consistent date display across the app
 * Format: dd-MMM-YYYY (e.g., 22-Dec-2025)
 */

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date string or Date object to dd-MMM-YYYY format
 * @param {string|Date} dateInput - Date string (YYYY-MM-DD, ISO, etc.) or Date object
 * @param {boolean} includeYear - Whether to include the year (default: true)
 * @returns {string} Formatted date string (e.g., "22-Dec-2025")
 */
export const formatDate = (dateInput, includeYear = true) => {
  if (!dateInput) return '—';
  
  try {
    let date;
    
    if (typeof dateInput === 'string') {
      // Handle YYYY-MM-DD format directly (avoid timezone issues)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        const [year, month, day] = dateInput.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateInput);
      }
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return '—';
    }
    
    if (isNaN(date.getTime())) {
      return '—';
    }
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = MONTH_NAMES[date.getMonth()];
    const year = date.getFullYear();
    
    return includeYear ? `${day}-${month}-${year}` : `${day}-${month}`;
  } catch (e) {
    return '—';
  }
};

/**
 * Format a date string or Date object to dd-MMM format (without year)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string (e.g., "22-Dec")
 */
export const formatDateShort = (dateInput) => {
  return formatDate(dateInput, false);
};

/**
 * Format a datetime string to dd-MMM-YYYY HH:mm format
 * @param {string|Date} dateInput - Datetime string or Date object
 * @returns {string} Formatted datetime string (e.g., "22-Dec-2025 14:30")
 */
export const formatDateTime = (dateInput) => {
  if (!dateInput) return '—';
  
  try {
    const date = new Date(dateInput);
    
    if (isNaN(date.getTime())) {
      return '—';
    }
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = MONTH_NAMES[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch (e) {
    return '—';
  }
};

/**
 * Format just the date portion for display (without time)
 * @param {string|Date} dateInput - Datetime string or Date object  
 * @returns {string} Formatted date string
 */
export const formatDateOnly = (dateInput) => {
  return formatDate(dateInput, true);
};

/**
 * Get day of week abbreviation
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Day abbreviation (e.g., "Mon", "Tue")
 */
export const getDayOfWeek = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [year, month, day] = dateInput.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateInput);
    }
    
    if (isNaN(date.getTime())) return '';
    
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  } catch (e) {
    return '';
  }
};
